import { setConfig } from './set_config.helper'
import { refreshToken, refreshProactiveToken } from '../actions/auth'

let refreshTimeout = null
let lastTokenTimestamp = null
let tokenPromise = null
const TOKEN_LIFETIME = 4.5 * 60 * 1000
const REFRESH_THRESHOLD = 3600 // 1 hour before expiry (in seconds)

// Get token from localStorage
const getStoredToken = () => {
  try {
    const token = window.localStorage.getItem('token')
    return token ? JSON.parse(token) : null
  } catch (error) {
    console.error('Error parsing stored token:', error)
    return null
  }
}

// Get user from localStorage
const getStoredUser = () => {
  try {
    const user = window.localStorage.getItem('user')
    return user ? JSON.parse(user) : null
  } catch (error) {
    console.error('Error parsing stored user:', error)
    return null
  }
}

// Decode JWT token (base64 decode)
const decodeToken = (token) => {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    return JSON.parse(window.atob(base64))
  } catch (error) {
    console.error('Error decoding token:', error)
    throw error
  }
}

// Check if token is expired or about to expire
const isTokenExpired = (token) => {
  if (!token) return true

  try {
    const decoded = decodeToken(token)
    const currentTime = Math.floor(Date.now() / 1000)
    const timeUntilExpiry = decoded.exp - currentTime

    // Return true if token expires within the refresh threshold
    return timeUntilExpiry < REFRESH_THRESHOLD
  } catch (error) {
    console.error('Error checking token expiration:', error)
    return true
  }
}

// Check if token is close to expiring (for proactive refresh)
const isTokenCloseToExpiry = (token) => {
  if (!token) return false

  try {
    const decoded = decodeToken(token)
    const currentTime = Math.floor(Date.now() / 1000)
    const timeUntilExpiry = decoded.exp - currentTime

    // Return true if token expires within 5 minutes
    return timeUntilExpiry < 300
  } catch (error) {
    console.error('Error checking token proximity to expiry:', error)
    return false
  }
}

// Refresh token and update localStorage (for expired tokens)
const refreshStoredToken = async () => {
  const currentToken = getStoredToken()
  const user = getStoredUser()

  if (!currentToken || !user) {
    throw new Error('No token or user found for refresh')
  }

  try {
    const response = await refreshToken(currentToken)
    if (response.data && response.data.token) {
      const newToken = response.data.token
      window.localStorage.setItem('token', JSON.stringify(newToken))
      return newToken
    } else {
      throw new Error('Invalid refresh response')
    }
  } catch (error) {
    console.error('Token refresh failed:', error)
    // Clear stored data on refresh failure
    window.localStorage.removeItem('token')
    window.localStorage.removeItem('user')
    window.localStorage.removeItem('client')
    throw error
  }
}

// Proactive token refresh (for valid tokens approaching expiry)
const refreshProactiveStoredToken = async () => {
  const currentToken = getStoredToken()
  const user = getStoredUser()

  if (!currentToken || !user) {
    throw new Error('No token or user found for proactive refresh')
  }

  try {
    const response = await refreshProactiveToken(currentToken)
    if (response.data && response.data.token) {
      const newToken = response.data.token
      window.localStorage.setItem('token', JSON.stringify(newToken))
      return newToken
    } else {
      throw new Error('Invalid proactive refresh response')
    }
  } catch (error) {
    console.error('Proactive token refresh failed:', error)
    // Don't clear data on proactive refresh failure, just log the error
    throw error
  }
}

// Clear refresh timeout
const clearRefreshTimeout = () => {
  if (refreshTimeout) {
    clearTimeout(refreshTimeout)
    refreshTimeout = null
  }
}

// Schedule token refresh
const scheduleRefresh = (token, delayMs) => {
  clearRefreshTimeout()

  refreshTimeout = setTimeout(async () => {
    try {
      // Check if token is close to expiry for proactive refresh
      if (isTokenCloseToExpiry(token)) {
        const newToken = await refreshProactiveStoredToken()
        onTokenRefreshed(newToken)
      } else {
        // Fallback to regular refresh if proactive fails
        const newToken = await refreshStoredToken()
        onTokenRefreshed(newToken)
      }
    } catch (error) {
      console.error('Token refresh failed:', error)
      onTokenExpired()
    }
  }, delayMs * 1000)
}

// Handle successful token refresh
const onTokenRefreshed = (newToken) => {
  // Store new token
  window.localStorage.setItem('token', JSON.stringify(newToken))

  // Update Redux state if available
  if (window.store) {
    window.store.dispatch({
      type: 'UPDATED_TOKEN',
      payload: newToken
    })
  }

  // Set up next refresh
  setupAutoRefresh(newToken)

  console.log('Token refreshed successfully')
}

// Handle token expiration
const onTokenExpired = () => {
  // Use the Redux action for logout
  if (window.store) {
    window.store.dispatch({ type: 'LOGOUT_USER' })
  } else {
    // Fallback if store is not available
    window.localStorage.removeItem('token')
    window.localStorage.removeItem('user')
    window.localStorage.removeItem('client')
    window.location.href = '/'
  }
}

// Set up automatic token refresh
export const setupAutoRefresh = (token) => {
  clearRefreshTimeout()

  try {
    const decoded = decodeToken(token)
    const currentTime = Math.floor(Date.now() / 1000)
    const timeUntilExpiry = decoded.exp - currentTime

    // If token expires within threshold, refresh it
    if (timeUntilExpiry > 0 && timeUntilExpiry < REFRESH_THRESHOLD) {
      scheduleRefresh(token, timeUntilExpiry - 300) // Refresh 5 minutes before expiry
    }
  } catch (error) {
    console.error('Error setting up auto-refresh:', error)
  }
}

export const getValidConfig = async (admin, token) => {
  const now = Date.now()

  // If there is no promise in progress and the current token is expired or does not exist
  if (!tokenPromise || (lastTokenTimestamp && (now - lastTokenTimestamp) > TOKEN_LIFETIME)) {
    // We create a new promise to get the token
    tokenPromise = setConfig(admin, token).then(config => {
      lastTokenTimestamp = Date.now()
      return config
    })
  }

  return tokenPromise
}

// Enhanced token manager with automatic refresh
export const getValidToken = async () => {
  const currentToken = getStoredToken()
  const user = getStoredUser()

  if (!currentToken || !user) {
    throw new Error('No token or user found')
  }

  // Check if token needs refresh
  if (isTokenExpired(currentToken)) {
    try {
      const newToken = await refreshStoredToken()
      return { token: newToken, user }
    } catch (error) {
      // If refresh fails, redirect to login
      window.location.href = '/'
      throw error
    }
  }

  return { token: currentToken, user }
}

// Initialize token refresh on app startup
export const initializeTokenRefresh = () => {
  const currentToken = getStoredToken()
  if (currentToken) {
    setupAutoRefresh(currentToken)
  }
}

// Cleanup function
export const destroyTokenManager = () => {
  clearRefreshTimeout()
}

// Make store available globally for token refresh
export const setGlobalStore = (store) => {
  window.store = store
}
